import { useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useWindowVirtualizer } from '@tanstack/react-virtual';
import { SideWithAsset } from 'shared-types';

import { buildSpreads, Spread } from 'components/BookLayout/buildSpreads';
import {
  calculateSpreadsPerRow,
  chunkSpreadsIntoRows,
  GRID_PADDING,
  ROW_GAP,
  SLOT_GAP,
  SLOT_WIDTH,
  SPREAD_GAP,
} from 'components/BookLayout/gridLayout';
import PageGridItem from 'components/BookLayout/PageGridItem';
import PagePlaceholder from 'components/BookLayout/PagePlaceholder';

import useSessionStore from 'hooks/stores/useSessionStore';
import useTrimboxCrop from 'hooks/useTrimboxCrop';

type Props = {
  sides: SideWithAsset[];
  selectedSideId?: string;
  // Blanco's die in deze sessie via de grid zijn ingevoegd; die mogen zonder bevestiging weg.
  insertedBlankSideIds: Set<string>;
  disabled: boolean;
  onOpenSide: (sideId: string) => void;
  onInsertBlankPageAt: (index: number) => void;
  // Niet gezet als verwijderen via de sessie-opties (ui.sideList.buttons.deleteSide) uit staat.
  onRemovePage?: (sideId: string) => void;
  // Upload vanaf de eerste vrije positie (klik of drop). Niet gezet als uploaden uit staat.
  onUploadFiles?: (files: File[]) => void;
};

// Hoogte van het paginanummer boven elk vakje (Text 0.8rem + gap 4). Alleen een schatting voor
// de virtualizer; de echte rijhoogte wordt na de eerste render per rij gemeten (measureElement).
const PAGE_LABEL_HEIGHT = 22;
// Rijen buiten beeld die alvast gerenderd worden, zodat scrollen niet flikkert.
const OVERSCAN_ROWS = 2;

// Gevirtualiseerd per rij (zoals de SidesList per item): dikke boeken hebben honderden pagina's
// en zonder virtualisatie hangen er evenveel <img>'s en IntersectionObservers in de DOM. De pagina
// zelf scrolt (window), dus de virtualizer krijgt de afstand van het grid tot de documenttop mee.
const PagesGrid = ({
  sides,
  selectedSideId,
  insertedBlankSideIds,
  disabled,
  onOpenSide,
  onInsertBlankPageAt,
  onRemovePage,
  onUploadFiles,
}: Props) => {
  const sidesCount = useSessionStore((state) => state.session?.sidesCount ?? 0);
  // Vakjes hebben de verhouding van de trimbox; de afbeelding erin wordt door PageGridItem op
  // dezelfde manier bijgesneden als in BookPreviewModal.
  const { aspectRatio } = useTrimboxCrop();
  const slotHeight = Math.round(SLOT_WIDTH * aspectRatio);

  const spreads = useMemo(() => buildSpreads(sides, sidesCount), [sides, sidesCount]);
  const isSingleSlotMode = spreads.length > 0 && spreads.every((spread) => spread.length === 1);
  const spreadWidth = isSingleSlotMode ? SLOT_WIDTH : SLOT_WIDTH * 2 + SLOT_GAP;

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(0);
  const [scrollMargin, setScrollMargin] = useState(0);
  // Het grid rendert pas als er setjes zijn; de meting moet dus (opnieuw) starten zodra de
  // container daadwerkelijk in de DOM staat, niet alleen bij de eerste mount.
  const hasSpreads = spreads.length > 0;

  useLayoutEffect(() => {
    const node = containerRef.current;
    if (!node) return;
    const update = (): void => {
      setContainerWidth(node.clientWidth);
      setScrollMargin(node.getBoundingClientRect().top + window.scrollY);
    };
    update();
    const observer = new ResizeObserver(update);
    observer.observe(node);
    // De toolbar erboven verandert van hoogte (progress bar, meldingen) → offset opnieuw meten.
    if (node.parentElement) observer.observe(node.parentElement);
    return () => observer.disconnect();
  }, [hasSpreads]);

  const spreadsPerRow = calculateSpreadsPerRow(containerWidth, spreadWidth);
  const rows = useMemo(
    () => chunkSpreadsIntoRows(spreads, spreadsPerRow),
    [spreads, spreadsPerRow],
  );

  const estimatedRowHeight = PAGE_LABEL_HEIGHT + slotHeight + ROW_GAP;
  const virtualizer = useWindowVirtualizer({
    count: rows.length,
    estimateSize: () => estimatedRowHeight,
    overscan: OVERSCAN_ROWS,
    scrollMargin,
  });

  // Aantal rijen of kolommen verandert (upload, verwijderen, resize): gemeten hoogtes zijn dan
  // niet meer geldig voor dezelfde index → opnieuw laten meten.
  useEffect(() => {
    virtualizer.measure();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows.length, spreadsPerRow, estimatedRowHeight]);

  // Eerste positie zonder side = waar een upload of nieuwe lege pagina terechtkomt (sides worden
  // achteraan toegevoegd).
  const firstFreeIndex = sides.length;

  const renderSpread = (spread: Spread, rowIndex: number, spreadIndex: number): JSX.Element => (
    <div
      key={`spread-${spread.find((slot) => slot !== null)?.index ?? `${rowIndex}-${spreadIndex}`}`}
      style={{ display: 'flex', alignItems: 'flex-end', gap: SLOT_GAP, flexShrink: 0 }}
    >
      {spread.map((slot, slotIndex) => {
        if (!slot) {
          // Lege plek (links van de cover, of rechts van een oneven laatste pagina) zodat de
          // pagina's op hun boekpositie blijven staan.
          return (
            <div
              key={`empty-${rowIndex}-${spreadIndex}-${slotIndex}`}
              style={{ width: SLOT_WIDTH }}
            />
          );
        }
        if (!slot.side) {
          return (
            <PagePlaceholder
              key={`placeholder-${slot.index}`}
              pageNumber={slot.index + 1}
              width={SLOT_WIDTH}
              height={slotHeight}
              isFirstFree={slot.index === firstFreeIndex}
              disabled={disabled}
              canUpload={Boolean(onUploadFiles)}
              onUploadFiles={(files) => onUploadFiles?.(files)}
              onInsertBlankPage={() => onInsertBlankPageAt(slot.index)}
            />
          );
        }
        const { side } = slot;
        return (
          <PageGridItem
            key={side.id}
            side={side}
            pageNumber={slot.index + 1}
            width={SLOT_WIDTH}
            height={slotHeight}
            isSelected={side.id === selectedSideId}
            disabled={disabled}
            onOpen={onOpenSide}
            onInsertBlankPage={() => onInsertBlankPageAt(slot.index)}
            onRemove={onRemovePage ? () => onRemovePage(side.id) : undefined}
            isInsertedBlank={insertedBlankSideIds.has(side.id)}
          />
        );
      })}
    </div>
  );

  if (!hasSpreads) return null;

  return (
    <div
      ref={containerRef}
      style={{ position: 'relative', width: '100%', height: virtualizer.getTotalSize() }}
    >
      {virtualizer.getVirtualItems().map((virtualRow) => (
        <div
          key={virtualRow.key}
          data-index={virtualRow.index}
          ref={virtualizer.measureElement}
          style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            boxSizing: 'border-box',
            padding: `0 ${GRID_PADDING}px ${ROW_GAP}px`,
            display: 'flex',
            alignItems: 'flex-end',
            gap: SPREAD_GAP,
            transform: `translateY(${virtualRow.start - virtualizer.options.scrollMargin}px)`,
          }}
        >
          {rows[virtualRow.index].map((spread, spreadIndex) =>
            renderSpread(spread, virtualRow.index, spreadIndex),
          )}
        </div>
      ))}
    </div>
  );
};

export default PagesGrid;
